/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react'
import { toast } from 'sonner'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useUnsuspendOwnerMutation } from '@/hooks/usePlatformUnsuspendOwner'
import AppDialog, { ModalActions } from '../../../components/CustomModal'

const schema = z.object({
  reason: z
    .string()
    .trim()
    .max(256, 'Max 256 caractères')
    .optional()
    .or(z.literal('')),
})

type Values = z.infer<typeof schema>

export function UnsuspendOwnerModal({
  ownerId,
  ownerName,
  disabled,
  onDone,
}: {
  ownerId: string
  ownerName?: string
  disabled?: boolean
  onDone?: () => void
}) {
  const [open, setOpen] = React.useState(false)
  const mut = useUnsuspendOwnerMutation(ownerId)

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { reason: '' },
  })

  const closeAndReset = () => {
    setOpen(false)
    // reset après fermeture, sinon parfois UI glitch sur certaines modals
    setTimeout(() => form.reset({ reason: '' }), 0)
  }

  const submit = async (v: Values) => {
    try {
      const res = await mut.mutateAsync({
        reason: v.reason?.trim() ? v.reason.trim() : undefined,
      })

      toast.success(res.already ? 'Déjà actif' : 'Owner réactivé', {
        description: ownerName ? ownerName : undefined,
      })

      closeAndReset()
      onDone?.()
    } catch (e: any) {
      toast.error('Réactivation impossible', {
        description: e?.response?.data?.message ?? 'Erreur inconnue',
      })
    }
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={(v) => {
        // si on ferme “manuellement”, reset
        if (!v) closeAndReset()
        else setOpen(true)
      }}
      title="Réactiver l'owner"
      description='Remet l’owner en état actif et supprime la suspension. (Le billing/renewal ne change pas.)'
      trigger={
        <Button
          variant='default'
          size='sm'
          disabled={disabled}
          onClick={() => setOpen(true)}
        >
          Unsuspend
        </Button>
      }
      // on ne met pas asForm ici, car ton ModalActions met type="button"
      // => on fait submit via handleSubmit() dans onConfirm.
      footer={
        <ModalActions
          cancelLabel='Annuler'
          confirmLabel='Réactiver'
          confirmVariant='default'
          confirmLoading={mut.isPending}
          confirmDisabled={!form.formState.isValid || mut.isPending}
          onConfirm={form.handleSubmit(submit)}
        />
      }
    >
      <Form {...form}>
        <div className='space-y-4'>
          <div className='rounded-lg border p-3 text-sm'>
            <div className='font-medium'>Owner</div>
            <div className='text-muted-foreground'>
              {ownerName ?? '—'} • <span className='font-mono'>{ownerId}</span>
            </div>
          </div>

          <FormField
            control={form.control}
            name='reason'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Raison (optionnel)</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder='Ex: Paiement reçu / suspension par erreur / ...'
                    autoFocus
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </Form>
    </AppDialog>
  )
}
